import VisuallyHidden from "@/blueprint/components/VisuallyHidden/VisuallyHidden";
import { MOTION_EASE_OUT, MotionDuration } from "@/blueprint/tokens/Motion";
import { cn } from "@/lib/utils";

const DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"] as const;
const isDigit = (char: string) => char >= "0" && char <= "9";

/**
 * One digit as an odometer wheel: a 0-9 strip behind a one-line window,
 * turned to the digit by a transform. The transition only runs when the
 * transform changes, so a digit that stays the same never moves.
 */
const DigitWheel = ({ digit }: { digit: number }) => (
  <span className="relative inline-block overflow-hidden">
    {/* Sizes the window to this digit's own glyph: the brand faces don't
        all carry tabular figures, so a "0"-wide window leaves a "1" adrift. */}
    <span className="invisible">{digit}</span>
    <span
      className="absolute inset-x-0 top-0 flex flex-col motion-reduce:!transition-none"
      style={{
        transform: `translateY(${-digit * 10}%)`,
        transition: `transform ${MotionDuration.mediumMs}ms ${MOTION_EASE_OUT}`,
      }}
    >
      {DIGITS.map((glyph) => (
        <span key={glyph}>{glyph}</span>
      ))}
    </span>
  </span>
);

/**
 * A value whose changed digits roll like an odometer ("$1,245" to "$1,343"
 * turns the hundreds and tens; "55 dB" to "52 dB" turns only the last digit).
 * Characters are matched from the right, where the units sit, so a figure
 * that gains or loses a place keeps its units aligned. Everything that is not
 * a digit, and every digit that did not change, holds still. Reduced motion
 * swaps the value outright.
 *
 * Inherits its type from the surrounding text; tabular figures keep the
 * wheels from shifting their neighbours.
 */
const RollingValue = ({
  value,
  className,
}: {
  value: string;
  className?: string;
}) => {
  const chars = [...value];

  return (
    <span className={cn("inline-flex tabular-nums", className)}>
      <span aria-hidden className="inline-flex">
        {chars.map((char, index) => {
          // Keyed by distance from the right and by kind, so a units digit
          // stays the same wheel across values while a digit that becomes a
          // separator is replaced rather than turned.
          const fromRight = chars.length - index;
          return isDigit(char) ? (
            <DigitWheel key={`d${fromRight}`} digit={Number(char)} />
          ) : (
            <span key={`c${fromRight}${char}`} className="whitespace-pre">
              {char}
            </span>
          );
        })}
      </span>
      <VisuallyHidden>{value}</VisuallyHidden>
    </span>
  );
};

export default RollingValue;
